import React from 'react';
import { Outlet, useLoaderData, useMatch, useRevalidator } from 'react-router-dom';
import { Await } from '@oysterjs/ui/async';

import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { Bars3Icon, BellIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  LoggedInAgencyUserFieldsFragment,
  SubscriptionPlan,
  SubscriptionPeriod,
  SubscriptionState,
  AgencyUserFieldsFragment,
  AgencyUserState,
  Resource,
  Action
} from '~/api/graphql';
import {
  ManageSubscriptionDialog,
  SelectPlanDialog,
  SubscriptionStatusBanner
} from '~/pages/settings/subscription';
import { DisableUserDialog } from '~/components/settings/subscription/user-dialogs';
import { DESIGN_EDIT_PATTERN, DESIGN_NEW_PATH } from '~/pages/settings/design-paths';
import { getStandardPlanRenewalDate } from '~/components/settings/subscription/renewal-billing-date';
import { RenewalMigrationWizard } from '~/components/settings/subscription/renewal-migration-wizard';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Button } from '@oysterjs/ui/button';
import { PermissionsProvider, usePermissions } from './permissions';
import { EntitlementsProvider } from './entitlements';
import { CurrentUserProvider } from './current-user';
import { client } from '~/api/client';
import { NewFeaturesModal } from './new-features';
import { LiveRefreshProvider, useLiveRefresh } from '@oysterjs/core/live-refresh';
import { Sidebar, SidebarNavigation, SidebarSkeleton, useSidebarCollapsed } from './sidebar';
import { UniversalSearchProvider } from './search';
import { FEATURE_FLAGS } from './feature-flags';
import { SmartFillOnlyPage } from './smart-fill-only';

// This is included just to capture the unlikely case that minSeats is not set in the subscription
// or the subscription is not found.
const FALLBACK_MIN_SEATS = 3;

export const DashboardContainer = () => {
  const loader = useLoaderData() as {
    data: Promise<{
      agencyUser: LoggedInAgencyUserFieldsFragment;
      agencyUsers: AgencyUserFieldsFragment[];
    }>;
  };

  return (
    <React.Suspense fallback={<DashboardContainerLoader />}>
      <Await resolve={loader.data}>
        {({ agencyUser, agencyUsers }) =>
          agencyUser.frontendFlags.includes(FEATURE_FLAGS.SMART_FILL_EXTENSION_ONLY) ? (
            <SmartFillOnlyPage />
          ) : (
            <LiveRefreshProvider>
              <CurrentUserProvider agencyUser={agencyUser} agencyUsers={agencyUsers}>
                <EntitlementsProvider agencyUser={agencyUser}>
                  <PermissionsProvider agencyUser={agencyUser}>
                    <Dashboard agencyUser={agencyUser} agencyUsers={agencyUsers} />
                  </PermissionsProvider>
                </EntitlementsProvider>
              </CurrentUserProvider>
            </LiveRefreshProvider>
          )
        }
      </Await>
    </React.Suspense>
  );
};

const Dashboard = ({
  agencyUser,
  agencyUsers
}: {
  agencyUser: LoggedInAgencyUserFieldsFragment;
  agencyUsers: AgencyUserFieldsFragment[];
}) => {
  const revalidator = useRevalidator();
  const { hasPermission } = usePermissions();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [showSelectPlanDialog, setShowSelectPlanDialog] = React.useState(false);
  const [showManageSubscriptionDialog, setShowManageSubscriptionDialog] = React.useState(false);
  const [manageSubscriptionPlan, setManageSubscriptionPlan] = React.useState<
    SubscriptionPlan | undefined
  >();
  const [manageSubscriptionPeriod, setManageSubscriptionPeriod] = React.useState<
    SubscriptionPeriod | undefined
  >();
  const [manageSubscriptionSeats, setManageSubscriptionSeats] = React.useState<
    number | undefined
  >();
  const [disableUserId, setDisableUserId] = React.useState<string | null>(null);
  const [showRenewalWizard, setShowRenewalWizard] = React.useState(false);
  const matchesWorkspace = useMatch('/app/workspaces/:id');
  // `:id` greedily matches the literal `archived` sub-route too, so the archived
  // workspaces list would otherwise collapse the sidebar like a detail view.
  const workspaceForcesRail = !!matchesWorkspace && matchesWorkspace.params.id !== 'archived';
  const editingDesign = !!useMatch(DESIGN_EDIT_PATTERN);
  const creatingDesign = !!useMatch(DESIGN_NEW_PATH);
  const { collapsed, setCollapsed, canToggle } = useSidebarCollapsed(
    workspaceForcesRail || editingDesign || creatingDesign
  );

  const [showProductDocsDialog, setShowProductDocsDialog] = React.useState(false);

  const onManageSubscription = async (
    plan: SubscriptionPlan,
    period: SubscriptionPeriod,
    seats?: number
  ) => {
    setManageSubscriptionPlan(plan);
    setManageSubscriptionPeriod(period);
    setManageSubscriptionSeats(seats);
    setShowSelectPlanDialog(false);
    setShowManageSubscriptionDialog(true);
  };

  if (agencyUser.agency.subscription?.state === SubscriptionState.Expired) {
    return (
      <div className="py-8 px-4 sm:py-12 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <SubscriptionStatusBanner
            subscription={agencyUser.agency.subscription}
            onUpdateSubscription={() => setShowSelectPlanDialog(true)}
            agencyUsers={agencyUsers}
            onDisableUser={(userId) => setDisableUserId(userId)}
            onReviewRenewalOptions={() => setShowRenewalWizard(true)}
            readOnly={!hasPermission(Resource.Subscription, Action.Update)}
          />
          <SelectPlanDialog
            open={showSelectPlanDialog}
            onClose={() => setShowSelectPlanDialog(false)}
            onSelectPlan={(plan, period, seats) =>
              onManageSubscription(
                plan,
                period,
                seats ||
                  agencyUser?.agency.subscription?.numSeats ||
                  Math.max(
                    agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                    agencyUsers?.filter((user) => user.state === AgencyUserState.Active).length || 0
                  )
              )
            }
            currentPlan={agencyUser.agency.subscription?.plan || SubscriptionPlan.Standard}
            currentPeriod={agencyUser.agency.subscription?.period || SubscriptionPeriod.Monthly}
            currentSeats={
              agencyUser.agency.subscription?.numSeats ||
              Math.max(
                agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
                agencyUsers?.filter((user) => user.state === AgencyUserState.Active).length || 0
              )
            }
            minSeats={Math.max(
              agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
              agencyUsers?.filter((user) => user.state === AgencyUserState.Active).length || 0
            )}
            includedSeats={agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS}
          />
          <ManageSubscriptionDialog
            open={showManageSubscriptionDialog}
            onClose={() => setShowManageSubscriptionDialog(false)}
            agencyUser={agencyUser}
            plan={
              manageSubscriptionPlan ||
              agencyUser?.agency.subscription?.plan ||
              SubscriptionPlan.Pro
            }
            period={
              manageSubscriptionPeriod ||
              agencyUser?.agency.subscription?.period ||
              SubscriptionPeriod.Monthly
            }
            seats={
              manageSubscriptionSeats ||
              agencyUser?.agency.subscription?.numSeats ||
              FALLBACK_MIN_SEATS
            }
            minSeats={Math.max(
              agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
              agencyUsers?.filter((user) => user.state === AgencyUserState.Active).length || 0
            )}
            includedSeats={agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS}
            voiceAgents={(agencyUser.agency.subscription?.numVoiceAgents || 1) - 1}
          />
          <DisableUserDialog
            client={client}
            open={!!disableUserId}
            userId={disableUserId}
            onClose={() => setDisableUserId(null)}
            onSuccess={() => {
              revalidator.revalidate();
            }}
            skipNavigation={true}
          />
        </div>
      </div>
    );
  }

  return (
    <UniversalSearchProvider>
      <Headless.Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className="relative z-50 lg:hidden"
      >
        <Headless.DialogBackdrop
          transition
          className="fixed inset-0 bg-neutral-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <Headless.DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <Headless.TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </Headless.TransitionChild>
            <SidebarNavigation
              onNavigate={() => setSidebarOpen(false)}
              onOpenDocumentation={() => {
                setSidebarOpen(false);
                setShowProductDocsDialog(true);
              }}
            />
          </Headless.DialogPanel>
        </div>
      </Headless.Dialog>

      <Sidebar
        collapsed={collapsed}
        onSetCollapsed={canToggle ? setCollapsed : undefined}
        onOpenDocumentation={() => setShowProductDocsDialog(true)}
      />

      <div
        className={clsx(
          'transition-all duration-150 ease-in-out',
          collapsed ? 'lg:pl-16' : 'lg:pl-64'
        )}
      >
        <div className="sticky top-0 z-10 flex lg:hidden h-16 shrink-0 items-center gap-x-4 border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            className="-m-2.5 p-2.5 text-neutral-700 dark:text-neutral-300 lg:hidden"
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>

          {/* Separator */}
          <div
            aria-hidden="true"
            className="h-6 w-px bg-neutral-200 dark:bg-neutral-700 lg:hidden"
          />

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <form action="#" method="GET" className="relative flex flex-1 opacity-0">
              <label htmlFor="search-field" className="sr-only">
                Search
              </label>
              <MagnifyingGlassIcon
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-neutral-400 dark:text-neutral-500"
              />
              <input
                id="search-field"
                name="search"
                type="search"
                placeholder="Search..."
                className="block h-full w-full border-0 py-0 pl-8 pr-0 text-neutral-900 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:ring-0 sm:text-sm"
              />
            </form>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <button
                type="button"
                className="-m-2.5 p-2.5 text-neutral-400 dark:text-neutral-500 hover:text-neutral-500 dark:hover:text-neutral-400 hidden"
              >
                <span className="sr-only">View notifications</span>
                <BellIcon aria-hidden="true" className="h-6 w-6" />
              </button>

              {/* Separator */}
              <div
                aria-hidden="true"
                className="hidden lg:block lg:h-6 lg:w-px lg:bg-neutral-200 dark:lg:bg-neutral-700"
              />

              {/* Profile dropdown */}
              <Headless.Menu as="div" className="relative">
                <Headless.MenuButton className="-m-1.5 flex items-center p-1.5">
                  <span className="sr-only">Open user menu</span>

                  <div className="rounded-full h-10 w-10 bg-primary-200/50 dark:bg-primary-500/25 flex items-center justify-center font-medium text-sm">
                    {agencyUser.name
                      .split(' ')
                      .map((name) => name[0])
                      .join('')
                      .toUpperCase()}
                  </div>
                  <span className="hidden lg:flex lg:items-center">
                    <span
                      aria-hidden="true"
                      className="ml-2 text-sm font-semibold leading-6 text-neutral-900 dark:text-white"
                    >
                      {agencyUser.name}
                    </span>
                    {/* <ChevronDownIcon
                aria-hidden="true"
                className="ml-2 h-5 w-5 text-neutral-400"
              /> */}
                  </span>
                </Headless.MenuButton>
              </Headless.Menu>
            </div>
          </div>
        </div>

        <main className="py-8 sm:py-12 bg-neutral-50 dark:bg-neutral-900 min-h-lvh">
          <div className="px-8 sm:px-12">
            <Outlet />
          </div>
        </main>
      </div>

      <RenewalMigrationWizard
        open={showRenewalWizard}
        onClose={() => {
          setShowRenewalWizard(false);
        }}
        onCompleted={() => {
          revalidator.revalidate();
        }}
        agencyUser={agencyUser}
        agencyName={agencyUser.agency.name}
        renewalDate={getStandardPlanRenewalDate(agencyUser.agency.subscription)}
        currentPeriod={agencyUser.agency.subscription?.period || SubscriptionPeriod.Monthly}
        currentSeats={
          agencyUser.agency.subscription?.numSeats ||
          Math.max(
            agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
            agencyUsers?.filter((user) => user.state === AgencyUserState.Active).length || 0
          )
        }
        minSeats={Math.max(
          agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS,
          agencyUsers?.filter((user) => user.state === AgencyUserState.Active).length || 0
        )}
        includedSeats={agencyUser?.agency.subscription?.numSeatsMinimum ?? FALLBACK_MIN_SEATS}
        voiceAgents={Math.max(0, (agencyUser.agency.subscription?.numVoiceAgents ?? 1) - 1)}
      />

      <ProductDocsDialog
        open={showProductDocsDialog}
        onClose={() => setShowProductDocsDialog(false)}
      />

      <NewFeaturesModal lastViewedNewFeatures={agencyUser.lastViewedNewFeatures} />

      <LiveRefreshModal />
    </UniversalSearchProvider>
  );
};

const DashboardContainerLoader = () => (
  <>
    <SidebarSkeleton />

    <div className="lg:pl-64">
      <div className="sticky top-0 z-10 flex lg:hidden h-16 shrink-0 items-center gap-x-4 border-b border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
        {/* Inert until the navigation loads, since there is nothing to open yet. */}
        <div
          aria-hidden="true"
          className="-m-2.5 p-2.5 text-neutral-300 dark:text-neutral-600 lg:hidden"
        >
          <Bars3Icon className="h-6 w-6" />
        </div>

        {/* Separator */}
        <div aria-hidden="true" className="h-6 w-px bg-neutral-200 dark:bg-neutral-700 lg:hidden" />

        <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
          <div className="relative flex flex-1" />
          <div className="rounded-full h-10 w-10 bg-primary-200/50 dark:bg-primary-500/25" />
        </div>
      </div>

      <main className="py-8 sm:py-12 bg-neutral-50 dark:bg-neutral-900 min-h-lvh">
        <div className="px-8 sm:px-12" />
      </main>
    </div>
  </>
);

const ProductDocsDialog = (props: { open: boolean; onClose: () => void }) => {
  return (
    <Dialog open={props.open} onClose={props.onClose} size="lg">
      <DialogTitle>Cara Documentation</DialogTitle>
      <DialogDescription>
        Get started with Cara and learn how to use it to its full potential.
      </DialogDescription>
      <DialogBody>
        <p className="text-sm">
          Access the Cara documentation by clicking the button below. You'll be asked to enter the
          following password to access the docs:
        </p>
        <div className="mt-4 flex flex-col gap-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 text-center p-4 rounded-lg font-mono">
          getcaraai
        </div>
      </DialogBody>
      <DialogActions>
        <Button color="white" onClick={props.onClose}>
          Close
        </Button>
        <Button color="sky" target="_blank" href="https://docs.getcara.ai" onClick={props.onClose}>
          Go to Documentation
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const LiveRefreshModal = () => {
  const { isNewVersionAvailable } = useLiveRefresh();

  // Whether the user has dismissed the notification.
  const [dismissed, setDismissed] = React.useState(false);

  // Add a 1.5 second delay before showing the notification. If a new version
  // is available immediately at page load, this draws the user's attention to the
  // notification since it will animate after the rest of the page has settled.
  const [showTimer, setShowTimer] = React.useState(false);
  React.useEffect(() => {
    if (!isNewVersionAvailable) {
      return;
    }

    const interval = setInterval(() => {
      setShowTimer(true);
    }, 1500);

    return () => clearInterval(interval);
  }, [isNewVersionAvailable]);

  return (
    <>
      {/* Global notification live region, render this permanently at the end of the document */}
      <div aria-live="assertive" className="pointer-events-none z-50 fixed inset-0 flex items-end">
        <div className="flex w-full flex-col items-center sm:items-end space-y-4">
          {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
          <Headless.Transition show={isNewVersionAvailable && showTimer && !dismissed}>
            <div className="pointer-events-auto w-full max-w-sm rounded-tl-lg rounded-tr-lg sm:rounded-tr-none bg-white/80 shadow-2xl outline outline-1 outline-black/5 backdrop-blur-xl transition data-[closed]:data-[enter]:translate-y-6 data-[enter]:transform data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:data-[enter]:sm:translate-y-6 dark:bg-gray-800/80 dark:-outline-offset-1 dark:outline-white/10">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="ml-3 w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      A new version of the app is available.
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Some features may not work correctly until you refresh the page. Click to
                      refresh now.
                    </p>
                    <div className="mt-4 flex">
                      <button
                        type="button"
                        className="inline-flex items-center rounded-md bg-primary-600 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:bg-primary-500 dark:shadow-none dark:hover:bg-primary-400 dark:focus-visible:outline-primary-500"
                        onClick={() => {
                          window.location.reload();
                        }}
                      >
                        Refresh
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setDismissed(true);
                        }}
                        className="ml-2 inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:shadow-none dark:ring-white/5 dark:hover:bg-white/20"
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Headless.Transition>
        </div>
      </div>
    </>
  );
};
